import React from 'react';
import { Link } from 'react-router-dom';

export interface PrimaryNavItem {
  key: string;
  label: string;
  to: string;
  active: boolean;
  priority: number;
  /** Only test layouts with nav icons pass one; without it the link is its label alone, as before. */
  icon?: React.ReactNode;
}

interface SelectVisiblePrimaryNavKeysOptions {
  items: PrimaryNavItem[];
  availableWidth: number;
  itemWidths: ReadonlyMap<string, number>;
  moreButtonWidth: number;
  gap: number;
}

export function selectVisiblePrimaryNavKeys({
  items,
  availableWidth,
  itemWidths,
  moreButtonWidth,
  gap,
}: SelectVisiblePrimaryNavKeysOptions): Set<string> {
  const totalItemsWidth = items.reduce((total, item) => total + (itemWidths.get(item.key) || 0), 0);
  const totalWidth = totalItemsWidth + Math.max(0, items.length - 1) * gap;
  if (totalWidth <= availableWidth) return new Set(items.map((item) => item.key));

  const availableForLinks = Math.max(0, availableWidth - moreButtonWidth - gap);
  const itemOrder = new Map(items.map((item, index) => [item.key, index]));
  const candidates = [...items].sort((left, right) => {
    if (left.active !== right.active) return left.active ? -1 : 1;
    if (left.priority !== right.priority) return right.priority - left.priority;
    return (itemOrder.get(left.key) || 0) - (itemOrder.get(right.key) || 0);
  });
  const visibleKeys = new Set<string>();
  let usedWidth = 0;

  candidates.forEach((item) => {
    const itemWidth = itemWidths.get(item.key) || 0;
    const nextWidth = usedWidth + (visibleKeys.size > 0 ? gap : 0) + itemWidth;
    if (nextWidth <= availableForLinks || visibleKeys.size === 0) {
      visibleKeys.add(item.key);
      usedWidth = nextWidth;
    }
  });

  return visibleKeys;
}

interface AdaptivePrimaryNavProps {
  id: string;
  items: PrimaryNavItem[];
  /** Keep every link; the layout lets the nav scroll sideways instead of moving links into More. */
  showAll?: boolean;
}

function sameKeys(left: Set<string>, right: Set<string>): boolean {
  if (left.size !== right.size) return false;
  for (const key of left) {
    if (!right.has(key)) return false;
  }
  return true;
}

export default function AdaptivePrimaryNav({ id, items, showAll = false }: AdaptivePrimaryNavProps) {
  const navRef = React.useRef<HTMLElement | null>(null);
  const measurementRef = React.useRef<HTMLDivElement | null>(null);
  const moreContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [visibleKeys, setVisibleKeys] = React.useState<Set<string>>(
    () => new Set(items.map((item) => item.key)),
  );
  const [moreOpen, setMoreOpen] = React.useState(false);

  React.useLayoutEffect(() => {
    const nav = navRef.current;
    const measurement = measurementRef.current;
    if (!nav || !measurement) return undefined;

    let frame = 0;
    let disposed = false;
    const measure = () => {
      if (disposed) return;
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        if (disposed) return;
        const itemWidths = new Map<string, number>();
        measurement.querySelectorAll<HTMLElement>('[data-nav-measure-key]').forEach((element) => {
          const key = element.dataset.navMeasureKey;
          if (key) itemWidths.set(key, element.getBoundingClientRect().width);
        });
        const moreButtonWidth = measurement
          .querySelector<HTMLElement>('[data-nav-measure-more]')
          ?.getBoundingClientRect().width || 0;
        const computedStyle = window.getComputedStyle(nav);
        // A column rail, or a layout whose nav scrolls sideways, keeps every link.
        if (showAll || computedStyle.flexDirection === 'column') {
          const allKeys = new Set(items.map((item) => item.key));
          setVisibleKeys((current) => (sameKeys(current, allKeys) ? current : allKeys));
          return;
        }
        const parsedGap = Number.parseFloat(computedStyle.columnGap || computedStyle.gap || '0');
        const nextKeys = selectVisiblePrimaryNavKeys({
          items,
          availableWidth: nav.clientWidth,
          itemWidths,
          moreButtonWidth,
          gap: Number.isFinite(parsedGap) ? parsedGap : 0,
        });
        setVisibleKeys((current) => (sameKeys(current, nextKeys) ? current : nextKeys));
      });
    };

    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(nav);
    window.addEventListener('resize', measure);
    void document.fonts?.ready.then(() => {
      if (!disposed) measure();
    });

    return () => {
      disposed = true;
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [items, showAll]);

  const visibleItems = React.useMemo(
    () => items.filter((item) => visibleKeys.has(item.key)),
    [items, visibleKeys],
  );
  const overflowItems = React.useMemo(
    () => items.filter((item) => !visibleKeys.has(item.key)),
    [items, visibleKeys],
  );

  React.useEffect(() => {
    if (overflowItems.length === 0) setMoreOpen(false);
  }, [overflowItems.length]);

  React.useEffect(() => {
    setMoreOpen(false);
  }, [items]);

  React.useEffect(() => {
    if (!moreOpen) return undefined;
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (target && !moreContainerRef.current?.contains(target)) setMoreOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMoreOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown, { capture: true });
    document.addEventListener('touchstart', handlePointerDown, { capture: true });
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown, { capture: true });
      document.removeEventListener('touchstart', handlePointerDown, { capture: true });
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [moreOpen]);

  return (
    <nav ref={navRef} id={id} className="topbar-nav" aria-label="Primary navigation">
      {visibleItems.map((item) => (
        <Link key={item.key} to={item.to} className={item.active ? 'active' : ''} title={item.icon ? item.label : undefined}>
          {item.icon ? (
            <>
              <span className="topbar-nav__icon" aria-hidden="true">{item.icon}</span>
              <span className="topbar-nav__label">{item.label}</span>
            </>
          ) : item.label}
        </Link>
      ))}
      {overflowItems.length > 0 && (
        <div className="topbar-nav__more" ref={moreContainerRef}>
          <a
            href="#more-navigation"
            role="button"
            className="topbar-nav__more-button"
            aria-haspopup="menu"
            aria-expanded={moreOpen}
            onClick={(event) => {
              event.preventDefault();
              setMoreOpen((open) => !open);
            }}
          >
            More
            <span className="topbar-nav__more-caret" aria-hidden="true">▾</span>
          </a>
          {moreOpen && (
            <div className="topbar-nav__more-menu" role="menu" aria-label="More destinations">
              {overflowItems.map((item) => (
                <Link
                  key={item.key}
                  to={item.to}
                  role="menuitem"
                  className={item.active ? 'active' : ''}
                  onClick={() => setMoreOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
      <div ref={measurementRef} className="topbar-nav__measurement" aria-hidden="true">
        {items.map((item) => (
          <a key={item.key} href={item.to} tabIndex={-1} data-nav-measure-key={item.key}>
            {item.label}
          </a>
        ))}
        <a href="#more-navigation" tabIndex={-1} data-nav-measure-more>More ▾</a>
      </div>
    </nav>
  );
}
